import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PushToggle } from "@/lib/push";

export default function AdminTelegram() {
    const [settings, setSettings] = useState(null);
    const [botInfo, setBotInfo] = useState(null);
    const [chatId, setChatId] = useState("");
    const [enabled, setEnabled] = useState(false);
    const [muted, setMuted] = useState(false);
    const [topics, setTopics] = useState({});
    const [discovered, setDiscovered] = useState(null);
    const [verifyRes, setVerifyRes] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState("");
    const [msg, setMsg] = useState(null);
    const [error, setError] = useState(null);

    const load = async () => {
        try {
            const s = await api.get("/admin/telegram/settings");
            setSettings(s.data);
            setChatId(s.data.chat_id || "");
            setEnabled(!!s.data.enabled);
            setMuted(!!s.data.muted);
            setTopics(s.data.topics || {});
            try {
                const b = await api.get("/admin/telegram/bot-info");
                setBotInfo(b.data);
            } catch { /* token issue surfaced elsewhere */ }
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setLoading(false); }
    };
    /* eslint-disable */
    useEffect(() => { load(); }, []);
    /* eslint-enable */

    const flash = (m) => { setMsg(m); setError(null); setTimeout(() => setMsg(null), 4000); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const payload = {
                enabled,
                muted,
                chat_id: chatId.trim() || null,
                topics: Object.fromEntries(
                    Object.entries(topics).map(([k, v]) => [k, v === "" || v == null ? null : parseInt(v)])
                ),
            };
            await api.put("/admin/telegram/settings", payload);
            flash("Impostazioni salvate.");
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const discover = async () => {
        setBusy("discover"); setError(null);
        try {
            const r = await api.get("/admin/telegram/discover");
            setDiscovered(r.data);
            if ((r.data.chats || []).length === 0) {
                flash("Nessun messaggio rilevato. Rendi il bot ADMIN del gruppo e scrivi un messaggio in ogni Topic, poi riprova.");
            }
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setBusy(""); }
    };

    const verify = async () => {
        setBusy("verify"); setError(null); setVerifyRes(null);
        try {
            const r = await api.get("/admin/telegram/verify");
            setVerifyRes(r.data);
            if (r.data.ok) {
                flash(r.data.migrated_to
                    ? `Gruppo migrato: chat_id aggiornato a ${r.data.migrated_to}.`
                    : `Connessione OK: "${r.data.title || r.data.chat_id}".`);
                await load();
            }
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setBusy(""); }
    };

    const sendTest = async (category) => {
        setBusy(`test-${category}`); setError(null);
        try {
            await api.post("/admin/telegram/test", { category });
            flash(`Messaggio di test inviato a "${categories[category]}".`);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setBusy(""); }
    };

    const categories = settings?.categories || {};
    const catKeys = Object.keys(categories);
    const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("it-IT") : "—";

    if (loading) return <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO…</div>;

    return (
        <div data-testid="admin-telegram">
            <div className="op-label text-t2bb6ff">// INTEGRAZIONE TELEGRAM</div>
            <h2 className="op-heading text-3xl mt-2">Notifiche <span className="op-gradient-text">Telegram</span></h2>
            <p className="text-t8f95a3 text-sm mt-2 max-w-2xl">
                Il bot pubblica le notifiche del sito nei <span className="text-tffffff">Topic</span> del gruppo Telegram.
                Configura il gruppo e mappa ogni categoria al suo Topic.
            </p>

            {/* Bot status */}
            <div className="op-card mt-6 p-5" style={{ borderTop: "3px solid rgb(var(--tc-2bb6ff))" }}>
                <div className="op-label text-[10px] text-t2bb6ff mb-2">STATO BOT</div>
                {botInfo ? (
                    <div className="op-mono text-sm text-tffffff">
                        @{botInfo.username} <span className="text-t7ce38b">● online</span>
                        <span className="text-t5c6370 ml-2">id {botInfo.id}</span>
                    </div>
                ) : (
                    <div className="op-mono text-sm text-tff4d4d">Token non valido o bot non raggiungibile</div>
                )}
            </div>

            {/* Connection health */}
            <div className="op-card mt-4 p-5" style={{ borderTop: "3px solid rgb(var(--tc-ff7a1a))" }} data-testid="telegram-health">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="op-label text-[10px] text-tff7a1a">STATO CONNESSIONE</div>
                    <button onClick={verify} disabled={busy === "verify"}
                        className="op-btn op-btn-ghost text-[10px]" data-testid="telegram-verify-btn">
                        {busy === "verify" ? "Verifica…" : "🩺 Verifica connessione"}
                    </button>
                </div>
                <div className="mt-3 space-y-1 op-mono text-xs">
                    <div>
                        {settings?.chat_id
                            ? <span className="text-t7ce38b">● Gruppo collegato <span className="text-t5c6370">({settings.chat_id})</span></span>
                            : <span className="text-tff4d4d">● Chat non collegata — usa «Rileva gruppo e Topic»</span>}
                    </div>
                    <div className="text-t8f95a3">Ultimo invio riuscito: <span className="text-tc9ccd3">{fmtTime(settings?.last_ok_at)}</span></div>
                    {settings?.last_error && (
                        <div className="text-tff8a65" data-testid="telegram-last-error">
                            Ultimo errore: «{settings.last_error}» <span className="text-t5c6370">({fmtTime(settings?.last_error_at)})</span>
                        </div>
                    )}
                    {settings?.migrated_at && (
                        <div className="text-tffc93c">
                            ⚠️ Gruppo migrato automaticamente da {settings.migrated_from} il {fmtTime(settings.migrated_at)} → chat_id aggiornato.
                        </div>
                    )}
                    {verifyRes && !verifyRes.ok && (
                        <div className="text-tff4d4d" data-testid="telegram-verify-result">Verifica fallita: {verifyRes.detail || verifyRes.reason}</div>
                    )}
                    {verifyRes && verifyRes.ok && (
                        <div className="text-t7ce38b" data-testid="telegram-verify-result">
                            {verifyRes.migrated_to ? verifyRes.detail : `Raggiungibile: "${verifyRes.title}" · può scrivere: ${verifyRes.can_send_messages ? "sì" : "no"}`}
                        </div>
                    )}
                </div>
                <div className="mt-4 pt-3 border-t border-t22252a">
                    <div className="op-mono text-[11px] text-tc9ccd3 mb-2">
                        🔔 Se le notifiche Telegram smettono di partire, gli admin ricevono un <span className="text-tffffff">avviso push</span>. Attiva le notifiche su questo dispositivo e premi «Invia test» per verificare che arrivino.
                    </div>
                    <PushToggle />
                </div>
            </div>

            {/* Setup guide */}
            <div className="op-card mt-4 p-5 text-sm text-tc9ccd3 space-y-1.5">
                <div className="op-label text-[10px] text-tff7a1a mb-1">COME COLLEGARE IL GRUPPO</div>
                <p>1. Crea un gruppo Telegram e attiva gli <b className="text-tffffff">Argomenti</b> (⚙️ Modifica → Argomenti).</p>
                <p>2. Aggiungi <b className="text-tffffff">@{botInfo?.username || "il bot"}</b> e rendilo <b className="text-tffffff">Amministratore</b>.</p>
                <p>3. Scrivi un messaggio in ogni Topic (Annunci, Mercato, Risultati, Squalifiche).</p>
                <p>4. Premi <b className="text-tffffff">Rileva gruppo e Topic</b> qui sotto e mappa le categorie.</p>
            </div>

            {/* Discover */}
            <div className="mt-6 flex items-center gap-3 flex-wrap">
                <button onClick={discover} disabled={busy === "discover"}
                    className="op-btn op-btn-primary text-xs" data-testid="telegram-discover-btn">
                    {busy === "discover" ? "Rilevamento…" : "🔍 Rileva gruppo e Topic"}
                </button>
                {chatId && <span className="op-mono text-xs text-t7ce38b">chat_id: {chatId}</span>}
            </div>

            {discovered && (
                <div className="op-card mt-4 p-5">
                    <div className="op-label text-[10px] text-t2bb6ff mb-3">GRUPPI RILEVATI</div>
                    {(discovered.chats || []).length === 0 ? (
                        <div className="text-t8f95a3 text-sm">{discovered.hint}</div>
                    ) : (
                        <div className="space-y-2">
                            {discovered.chats.map((c) => (
                                <div key={c.chat_id} className="flex items-center justify-between gap-3 border border-t22252a px-3 py-2">
                                    <div>
                                        <div className="text-tffffff text-sm">{c.title || "(senza nome)"} {c.is_forum && <span className="op-label text-[9px] text-tff7a1a ml-1">FORUM</span>}</div>
                                        <div className="op-mono text-[10px] text-t5c6370">{c.chat_id} · {c.type}</div>
                                    </div>
                                    <button onClick={() => setChatId(c.chat_id)}
                                        className="op-btn op-btn-ghost text-[10px]" data-testid={`use-chat-${c.chat_id}`}>
                                        Usa questo gruppo
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {(discovered.topics || []).length > 0 && (
                        <>
                            <div className="op-label text-[10px] text-tff7a1a mt-5 mb-3">TOPIC RILEVATI — assegna a una categoria</div>
                            <div className="space-y-2">
                                {discovered.topics.map((t) => (
                                    <div key={`${t.chat_id}:${t.thread_id}`} className="flex items-center justify-between gap-3 border border-t22252a px-3 py-2">
                                        <div className="text-tffffff text-sm">{t.name}
                                            <span className="op-mono text-[10px] text-t5c6370 ml-2">thread {t.thread_id}</span>
                                        </div>
                                        <select
                                            defaultValue=""
                                            onChange={(e) => { if (e.target.value) setTopics({ ...topics, [e.target.value]: t.thread_id }); }}
                                            className="bg-t0e1014 border border-t22252a text-tffffff text-xs px-2 py-1.5"
                                            data-testid={`assign-topic-${t.thread_id}`}
                                        >
                                            <option value="">Assegna a…</option>
                                            {catKeys.map((k) => <option key={k} value={k}>{categories[k]}</option>)}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Manual config */}
            <div className="op-card mt-6 p-5">
                <div className="op-label text-[10px] text-t2bb6ff mb-3">CONFIGURAZIONE</div>

                <label className="op-label text-[10px] text-t8f95a3">CHAT ID DEL GRUPPO</label>
                <input
                    value={chatId}
                    onChange={(e) => setChatId(e.target.value)}
                    placeholder="-1001234567890"
                    className="w-full mt-1 mb-4 bg-t0e1014 border border-t22252a text-tffffff op-mono text-sm px-3 py-2"
                    data-testid="telegram-chat-id"
                />

                <label className="op-label text-[10px] text-t8f95a3">TOPIC PER CATEGORIA (message_thread_id)</label>
                <div className="space-y-2 mt-2">
                    {catKeys.map((k) => (
                        <div key={k} className="flex items-center gap-3">
                            <span className="text-sm text-tffffff min-w-[210px]">{categories[k]}</span>
                            <input
                                type="number"
                                value={topics[k] ?? ""}
                                onChange={(e) => setTopics({ ...topics, [k]: e.target.value })}
                                placeholder="thread id (vuoto = topic Generale)"
                                className="flex-1 bg-t0e1014 border border-t22252a text-tffffff op-mono text-sm px-3 py-1.5"
                                data-testid={`telegram-topic-${k}`}
                            />
                            <button onClick={() => sendTest(k)} disabled={busy === `test-${k}` || !chatId}
                                className="op-btn op-btn-ghost text-[10px] whitespace-nowrap" data-testid={`telegram-test-${k}`}>
                                {busy === `test-${k}` ? "…" : "Test"}
                            </button>
                        </div>
                    ))}
                </div>

                <label className="flex items-center gap-2 mt-5 cursor-pointer">
                    <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
                        className="accent-t2bb6ff w-4 h-4" data-testid="telegram-enabled" />
                    <span className="text-sm text-tffffff">Notifiche attive</span>
                </label>

                <label className="flex items-center gap-2 mt-3 cursor-pointer" data-testid="telegram-muted-label">
                    <input type="checkbox" checked={muted} onChange={(e) => setMuted(e.target.checked)}
                        className="accent-tff7a1a w-4 h-4" data-testid="telegram-muted" />
                    <span className="text-sm text-tffffff">🔕 Silenzia notifiche <span className="text-t8f95a3">(modalità test/simulazione — non invia nulla al gruppo)</span></span>
                </label>
                {muted && (
                    <div className="op-card mt-2 p-2.5 text-xs" style={{ borderLeft: "2px solid rgb(var(--tc-ff7a1a))" }}>
                        <span className="text-tff7a1a">Notifiche silenziate.</span> <span className="text-tc9ccd3">Le automazioni non scriveranno nel gruppo finché non togli la spunta. Il pulsante «Test» qui sopra funziona comunque.</span>
                    </div>
                )}

                <div className="mt-5 flex items-center gap-3">
                    <button onClick={save} disabled={saving}
                        className="op-btn op-btn-primary text-xs" data-testid="telegram-save-btn">
                        {saving ? "Salvataggio…" : "Salva impostazioni"}
                    </button>
                    {msg && <span className="text-t7ce38b text-sm" data-testid="telegram-msg">{msg}</span>}
                </div>
            </div>

            {error && <div className="text-tff4d4d text-sm mt-4" data-testid="telegram-error">{error}</div>}
        </div>
    );
}
